import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { LocateFixed, Loader2 } from "lucide-react";

export default function PickerMap({ lat, lng, onChange, onGps, autoLocate, relocateSignal }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const circleRef = useRef(null);
  const changeRef = useRef(onChange);
  const gpsRef = useRef(onGps);
  const [locating, setLocating] = useState(false);
  changeRef.current = onChange;
  gpsRef.current = onGps;

  const drawAccuracy = (la, ln, acc) => {
    const map = mapRef.current;
    if (!map) return;
    if (circleRef.current) {
      map.removeLayer(circleRef.current);
      circleRef.current = null;
    }
    if (acc && acc > 0) {
      circleRef.current = L.circle([la, ln], {
        radius: acc,
        color: "#D97706",
        weight: 1,
        fillColor: "#D97706",
        fillOpacity: 0.12,
      }).addTo(map);
    }
  };

  const doLocate = (silent = false) => {
    if (!navigator.geolocation || !mapRef.current) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const { latitude, longitude, accuracy, altitude } = p.coords;
        mapRef.current.setView([latitude, longitude], 18);
        drawAccuracy(latitude, longitude, accuracy);
        gpsRef.current &&
          gpsRef.current({ lat: latitude, lng: longitude, accuracy, altitude });
        setLocating(false);
      },
      () => {
        setLocating(false);
        if (!silent)
          alert("Standort konnte nicht ermittelt werden. Bitte Standortfreigabe erlauben.");
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  useEffect(() => {
    if (mapRef.current) return;
    const hasStart = lat != null && lng != null;
    const center = hasStart ? [lat, lng] : [51.1657, 10.4515];
    const map = L.map(elRef.current, {
      zoomControl: true,
      attributionControl: false,
    }).setView(center, hasStart ? 17 : 6);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);

    map.on("moveend", () => {
      const c = map.getCenter();
      changeRef.current &&
        changeRef.current(Number(c.lat.toFixed(6)), Number(c.lng.toFixed(6)));
    });

    mapRef.current = map;
    setTimeout(() => {
      map.invalidateSize();
      const c = map.getCenter();
      changeRef.current &&
        changeRef.current(Number(c.lat.toFixed(6)), Number(c.lng.toFixed(6)));
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoLocate) {
      const t = setTimeout(() => doLocate(true), 300);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch GPS when the parent bumps the signal (e.g. after saving in the create loop)
  useEffect(() => {
    if (relocateSignal) doLocate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relocateSignal]);

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-slate-200 isolate z-0">
      <div ref={elRef} className="gomap absolute inset-0" data-testid="picker-map" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 z-[500] -translate-x-1/2 -translate-y-full">
        <div className="go-center-pin" style={{ "--pin": "#D97706" }} />
      </div>
      <button
        type="button"
        onClick={doLocate}
        data-testid="locate-me-button"
        className="absolute bottom-3 right-3 z-[500] flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-lg transition-transform hover:scale-[1.03] active:scale-[0.97]"
      >
        {locating ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <LocateFixed size={16} className="text-primary" />
        )}
        Standort
      </button>
    </div>
  );
}
